import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - 60px);
  padding: 2rem 1rem;
  position: relative;
  overflow: hidden;
  text-align: center;

  @media (max-width: 480px) {
    min-height: calc(100vh - 120px);
    padding: 1rem 0;
  }
`;

export const ScanLine = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, transparent, ${({ theme }) => theme.colors.blue4}, transparent);
  opacity: 0.35;
  pointer-events: none;
  z-index: 0;
`;

export const ContentWrapper = styled.div`
  max-width: 720px;
  width: 100%;
  background: ${({ theme }) => theme.colors.blue2};
  border: 1px solid ${({ theme }) => theme.colors.blue3};
  border-radius: 12px;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.24);
  padding: clamp(1.35rem, 4vw, 2.25rem);
  z-index: 1;
  animation: ${fadeUp} 0.6s ease-out both;

  @media (max-width: 480px) {
    border-radius: 10px;
    padding: 1.15rem;
  }
`;

export const TimecodeBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-family: 'Courier New', monospace;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.blue11};
  letter-spacing: 0.1em;
  margin-bottom: 1.15rem;
  opacity: 0.75;

  @media (max-width: 480px) {
    font-size: 0.65rem;
    gap: 0.35rem;
  }
`;

export const TimecodeSegment = styled.span<{ highlight?: boolean }>`
  color: ${({ highlight, theme }) => highlight ? theme.colors.blue9 : theme.colors.blue11};
`;

export const ErrorCode = styled.div`
  position: relative;
  font-size: clamp(4.75rem, 15vw, 8rem);
  font-weight: 800;
  line-height: 0.9;
  margin-bottom: 1.1rem;
  color: ${({ theme }) => theme.colors.blue9};
  letter-spacing: -0.04em;
  text-shadow: 0 1px 0 ${({ theme }) => theme.colors.blue4};

  &::before,
  &::after {
    display: none;
  }
`;

export const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: ${({ theme }) => theme.colors.blue3};
  border: 1px solid ${({ theme }) => theme.colors.blue4};
  color: ${({ theme }) => theme.colors.blue10};
  padding: 0.4rem 0.85rem;
  border-radius: 100px;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 1rem;

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.blue8};
  }
`;

export const ErrorTitle = styled.h1`
  font-size: clamp(1.45rem, 3vw, 2rem);
  font-weight: 700;
  color: ${({ theme }) => theme.colors.blue9};
  margin-bottom: 0.6rem;
  line-height: 1.2;
`;

export const ErrorDescription = styled.p`
  font-size: 0.95rem;
  color: ${({ theme }) => theme.colors.blue11};
  margin-bottom: 1.6rem;
  line-height: 1.6;
  max-width: 470px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

export const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
`;

export const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'Poppins', sans-serif;
  background: ${({ theme }) => theme.colors.blue9};
  color: ${({ theme }) => theme.colors.blue1};
  border: 1px solid ${({ theme }) => theme.colors.blue9};

  i { font-size: 1.1rem; }

  &:hover {
    background: ${({ theme }) => theme.colors.blue10};
    border-color: ${({ theme }) => theme.colors.blue10};
    transform: translateY(-2px);
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.22);
  }

  &:active { transform: translateY(0); }
`;

export const SecondaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'Poppins', sans-serif;
  background: transparent;
  color: ${({ theme }) => theme.colors.blue9};
  border: 1px solid ${({ theme }) => theme.colors.blue4};

  i { font-size: 1.1rem; }

  &:hover {
    background: ${({ theme }) => theme.colors.blue2};
    border-color: ${({ theme }) => theme.colors.blue5};
    transform: translateY(-2px);
  }

  &:active { transform: translateY(0); }
`;

export const Divider = styled.div`
  width: 100%;
  height: 1px;
  background: ${({ theme }) => theme.colors.blue4};
  margin: 0 auto 1.5rem;
`;

export const SuggestedLinks = styled.div``;

export const SuggestedTitle = styled.p`
  font-size: 0.72rem;
  color: ${({ theme }) => theme.colors.blue11};
  text-transform: uppercase;
  letter-spacing: 0.12em;
  margin-bottom: 0.85rem;
`;

export const SuggestedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.75rem;

  @media (max-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

export const SuggestedLink = styled(Link)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 0.95rem 0.75rem;
  background: ${({ theme }) => theme.colors.blue1};
  border: 1px solid ${({ theme }) => theme.colors.blue4};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.blue9};
  text-decoration: none;
  font-size: 0.82rem;
  font-weight: 700;
  transition: all 0.2s ease;

  i {
    font-size: 1.4rem;
    color: ${({ theme }) => theme.colors.blue8};
  }

  &:hover {
    background: ${({ theme }) => theme.colors.blue3};
    border-color: ${({ theme }) => theme.colors.blue4};
    transform: translateY(-2px);

    i { color: ${({ theme }) => theme.colors.blue9}; }
  }
`;

/* Legacy stubs — no longer rendered but kept so imports don't break */
export const IllustrationWrapper = styled.div`display: none;`;
export const TimelineBar = styled.div`display: none;`;
export const TimelineCursor = styled.div`display: none;`;
export const MissingClipText = styled.div`display: none;`;
